import { getBranding, isWhiteLabel } from "@/branding";
import UserFacingLayout from "@/components/layout/UserFacingLayout.vue";
import { portalNavigationRedirect } from "@/portals";
import { markAccountStaleOnPageChange } from "@/composables/account/useAccountStore";
import {
  isAdminAccount,
  loadAccountForGate,
} from "@/composables/useAdminCheck";
import DashboardView from "@/dashboard/DashboardView.vue";
import EcolabelsView from "@/ecolabels/EcolabelsView.vue";
import MarketInsightsView from "@/market_insights/MarketInsightsView.vue";
import SolderaInventoryLayout from "@/soldera_inventory/SolderaInventoryLayout.vue";
import SolderaInventoryForwardView from "@/soldera_inventory/forward/SolderaInventoryForwardView.vue";
import SolderaInventorySpotView from "@/soldera_inventory/spot/SolderaInventorySpotView.vue";
import CallbackView from "@/root/CallbackView.vue";
import RootLayout from "@/root/RootLayout.vue";
import RootView from "@/root/RootView.vue";
import ProducerSignupView from "@/signup/ProducerSignupView.vue";
import RoleSelectionView from "@/signup/RoleSelectionView.vue";
import SignUpLayout from "@/signup/SignUpLayout.vue";
import CorporateSignupView from "@/signup/corporate/CorporateSignupView.vue";
import FIFlowSelection from "@/signup/fi/FIFlowSelection.vue";
import FISignupFlowSteps from "@/signup/fi/FISignupFlowSteps.vue";
import SettingsView from "@/user_settings/SettingsView.vue";
// WORKAROUND: no lazy route imports. In production, /static/* on the app host
// 301-redirects cross-origin to static.soldera.org; a lazy chunk then imports
// shared code back from ./main-*.js relative to the REDIRECTED origin, which
// misses the browser's module map (main is registered under the pre-redirect
// URL) and re-executes the entire main bundle. The second createApp().mount()
// wipes the live app's DOM and crashes with "Cannot read properties of null
// (reading 'nextSibling')" — a white page on any route whose chunk loads.
// This is also why the admin routes below were "converted from lazy loading".
// Goes away if index.html ever references assets on the static origin
// directly (no redirect) — until then, keep every route statically imported.
import DeviceUploadView from "@/ecolabels/upload/DeviceUploadView.vue";
import ComplianceGroupsLayout from "@/compliance_groups/ComplianceGroupsLayout.vue";
import ComplianceGroupView from "@/compliance_groups/ComplianceGroupView.vue";
import DomainWorkspaceView from "@/compliance_groups/DomainWorkspaceView.vue";
import PpaDetailView from "@/compliance_groups/PpaDetailView.vue";
import RequestSubmittedView from "@/compliance_groups/RequestSubmittedView.vue";
import SourceRequestView from "@/compliance_groups/SourceRequestView.vue";

import DealDetailView from "@/forwards2/deals/DealDetailView.vue";
import ForwardsOverviewView from "@/forwards2/overview/ForwardsOverviewView.vue";
import PapDealWizardView from "@/forwards2/picker/PapDealWizardView.vue";
import VolumePickerView from "@/forwards2/picker/VolumePickerView.vue";
import { authGuard } from "@auth0/auth0-vue";
import { nextTick } from "vue";
import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";
// Admin imports - converted from lazy loading
import AccountsView from "@/admin/AccountsView.vue";
import AdminIndex from "@/admin/AdminIndex.vue";
import AdminAiConversationsView from "@/admin/ai-conversations/AdminAiConversationsView.vue";
import AdminTradeChatLogsView from "@/admin/trade-chat-logs/AdminTradeChatLogsView.vue";
import AdminRegistryDevicesView from "@/admin/AdminRegistryDevicesView.vue";
import AllocationView from "@/admin/AllocationView.vue";
import ApiKeysView from "@/admin/ApiKeysView.vue";
import AdminCompanyOrdersView from "@/admin/company-orders/AdminCompanyOrdersView.vue";
import AdminDashboardView from "@/admin/DashboardView.vue";
import EcolabelManagementView from "@/admin/EcolabelManagementView.vue";
import EmailTemplatesView from "@/admin/EmailTemplatesView.vue";
import EmailsView from "@/admin/EmailsView.vue";
import GlobalCompaniesView from "@/admin/GlobalCompaniesView.vue";
import MapView from "@/admin/MapView.vue";
import CommissionView from "@/admin/commission/CommissionView.vue";
import IssuanceView from "@/admin/issuance/IssuanceView.vue";
import EsoInfoView from "@/admin/integrations/lt-eso/EsoInfoView.vue";
import EsoMappingView from "@/admin/integrations/lt-eso/EsoMappingView.vue";
import EsoPowerBiView from "@/admin/integrations/lt-eso/EsoPowerBiView.vue";
import EsoStatusView from "@/admin/integrations/lt-eso/EsoStatusView.vue";
import FinancialDashboardView from "@/admin/financial-dashboard/FinancialDashboardView.vue";
import AdminPortfoliosView from "@/admin/portfolios/AdminPortfoliosView.vue";
import CertificateChainView from "@/admin/certificate-chain/CertificateChainView.vue";
import VirtualPortfolioAssignmentView from "@/admin/virtual-accounts/VirtualPortfolioAssignmentView.vue";
import EacPricingManagementView from "@/market_insights/admin/EacPricingManagementView.vue";
import BillingView from "@/money/admin/billing/BillingView.vue";
import TransactionsListView from "@/money/admin/transactions/TransactionsListView.vue";
import TransactionsNewView from "@/money/admin/transactions/TransactionsNewView.vue";
import TransactionPerformanceView from "@/money/admin/transactions/performance/TransactionPerformanceView.vue";
import AdminRegistriesView from "@/registry/admin/AdminRegistriesView.vue";

/** `beforeEnter` for the admin pages: anyone without admin rights lands on the dashboard. */
const adminGuard = async () =>
  isAdminAccount(await loadAccountForGate()) ? true : { name: "Dashboard" };

const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    component: RootLayout,
    beforeEnter: [authGuard],
    children: [
      {
        path: "",
        name: "Root",
        // RootView loads the account only to pick the landing page, which reuses that load.
        meta: { title: "Soldera", passesAccountOn: true },
        component: RootView,
        beforeEnter: [authGuard],
      },
      {
        path: "settings",
        name: "Settings",
        meta: { title: "Soldera" },
        component: SettingsView,
        beforeEnter: [authGuard],
      },
      {
        path: "dashboard",
        meta: { title: "Soldera" },
        component: UserFacingLayout,
        beforeEnter: [authGuard],
        children: [
          {
            path: "",
            name: "Dashboard",
            component: DashboardView,
            beforeEnter: [authGuard],
          },
        ],
      },
      {
        path: "market-insights",
        meta: { title: "Soldera - Market Insights" },
        component: UserFacingLayout,
        beforeEnter: [authGuard],
        children: [
          {
            path: "",
            name: "MarketInsights",
            component: MarketInsightsView,
            beforeEnter: [authGuard],
          },
        ],
      },
      {
        path: "soldera-inventory",
        meta: { title: "Soldera - Soldera Inventory" },
        component: UserFacingLayout,
        beforeEnter: [authGuard],
        children: [
          {
            // One layout around every kind of inventory; each kind is a child route (a tab).
            path: "",
            component: SolderaInventoryLayout,
            beforeEnter: [authGuard],
            children: [
              {
                path: "",
                name: "SolderaInventory",
                redirect: { name: "SolderaInventorySpot" },
              },
              {
                path: "spot",
                name: "SolderaInventorySpot",
                component: SolderaInventorySpotView,
              },
              {
                path: "forward",
                name: "SolderaInventoryForward",
                component: SolderaInventoryForwardView,
              },
            ],
          },
        ],
      },
      {
        path: "ecolabels",
        meta: { title: "Soldera - Ecolabels" },
        component: UserFacingLayout,
        beforeEnter: [authGuard],
        children: [
          {
            path: "",
            name: "Ecolabels",
            component: EcolabelsView,
            beforeEnter: [authGuard],
          },
          {
            path: "upload",
            name: "EcolabelDeviceUpload",
            component: DeviceUploadView,
            beforeEnter: [authGuard],
          },
        ],
      },
      {
        path: "compliance-groups",
        meta: { title: "Soldera - Compliance Groups" },
        component: UserFacingLayout,
        beforeEnter: [authGuard],
        children: [
          {
            path: ":group",
            component: ComplianceGroupsLayout,
            children: [
              {
                path: "",
                name: "ComplianceGroup",
                component: ComplianceGroupView,
              },
              {
                path: "ppas/:ppa",
                name: "ComplianceGroupPpa",
                component: PpaDetailView,
              },
              {
                path: "domains/:domain",
                name: "ComplianceGroupDomain",
                component: DomainWorkspaceView,
              },
              {
                path: "source-request",
                name: "ComplianceGroupSourceRequest",
                component: SourceRequestView,
              },
              {
                path: "source-request/submitted",
                name: "ComplianceGroupRequestSubmitted",
                component: RequestSubmittedView,
              },
            ],
          },
        ],
      },
      {
        path: "signup",
        meta: { title: "Soldera" },
        component: SignUpLayout,
        beforeEnter: [authGuard],
        children: [
          {
            path: "",
            name: "RoleSelection",
            component: RoleSelectionView,
            beforeEnter: [authGuard],
          },
          {
            path: "producer",
            name: "ProducerSignUp",
            component: ProducerSignupView,
            beforeEnter: [authGuard],
          },
          {
            path: "corporate",
            name: "CorporateSignUp",
            component: CorporateSignupView,
            beforeEnter: [authGuard],
          },
          // Finland Specific Implementation
          {
            path: "fi",
            children: [
              {
                path: "",
                component: FIFlowSelection,
                name: "FISignupFlowSelection",
                beforeEnter: [authGuard],
              },
              {
                path: ":flow/:step?",
                component: FISignupFlowSteps,
                name: "FISignupFlow",
                beforeEnter: [authGuard],
              },
            ],
          },
        ],
      },
    ],
  },
  {
    // The forwards pages lived at /forwards until they became staff-only; links already sent in
    // emails and Slack, and bookmarks, still point there.
    path: "/forwards/:rest(.*)*",
    redirect: (to) => ({
      path: `/admin${to.path}`,
      query: to.query,
      hash: to.hash,
    }),
  },
  {
    path: "/admin",
    component: RootLayout,
    beforeEnter: [authGuard, adminGuard],
    children: [
      {
        // Forwards v2 is staff-only (user decision, 2026-10-12): the admin record's guards admit the
        // account, and each page reads what it may do off the role.
        path: "forwards",
        meta: { title: "Soldera - Forward sales" },
        children: [
          {
            path: "",
            name: "ForwardsOverview",
            component: ForwardsOverviewView,
          },
          {
            path: "deals/:id",
            name: "ForwardsDeal",
            meta: { title: "Soldera - Forward deal" },
            component: DealDetailView,
          },
          {
            path: "pay-as-produced/new",
            name: "ForwardsPapDealNew",
            meta: { title: "Soldera - New pay-as-produced deal" },
            component: PapDealWizardView,
          },
        ],
      },
      {
        path: "",
        name: "AdminIndex",
        meta: { title: "Admin - Soldera" },
        component: AdminIndex,
        beforeEnter: [authGuard, adminGuard],
      },
      {
        path: "ai-assistant/conversations",
        name: "AdminAiConversationsView",
        meta: { title: "AI Conversations - Admin - Soldera" },
        component: AdminAiConversationsView,
        beforeEnter: [authGuard, adminGuard],
      },
      {
        path: "trade-chat-logs",
        name: "AdminTradeChatLogsView",
        meta: { title: "Trade Chat Logs - Admin - Soldera" },
        component: AdminTradeChatLogsView,
        beforeEnter: [authGuard, adminGuard],
      },
      {
        path: "portfolios",
        name: "AdminPortfoliosView",
        meta: { title: "Portfolios - Admin - Soldera" },
        component: AdminPortfoliosView,
        beforeEnter: [authGuard, adminGuard],
      },
      {
        path: "certificate-chain",
        name: "AdminCertificateChainView",
        meta: { title: "Certificate Chain - Admin - Soldera" },
        component: CertificateChainView,
        beforeEnter: [authGuard, adminGuard],
      },
      {
        path: "virtual-accounts",
        name: "AdminVirtualAccountsAssignmentView",
        meta: { title: "Hosted Accounts Assignment - Admin - Soldera" },
        component: VirtualPortfolioAssignmentView,
        beforeEnter: [authGuard, adminGuard],
      },
      {
        path: "accounts",
        name: "AdminAccountsView",
        meta: { title: "Accounts - Admin - Soldera" },
        component: AccountsView,
        beforeEnter: [authGuard, adminGuard],
      },
      {
        path: "allocation",
        name: "AdminAllocationView",
        meta: { title: "Allocation - Admin - Soldera" },
        component: AllocationView,
        beforeEnter: [authGuard, adminGuard],
      },
      {
        path: "api-keys",
        name: "AdminApiKeysView",
        meta: { title: "Admin - API Keys" },
        component: ApiKeysView,
        beforeEnter: [authGuard, adminGuard],
      },
      {
        path: "emails",
        name: "AdminEmailsView",
        meta: { title: "Emails - Admin - Soldera" },
        component: EmailsView,
        beforeEnter: [authGuard, adminGuard],
      },
      {
        path: "email-templates",
        name: "AdminEmailTemplatesView",
        meta: { title: "Email Templates - Admin - Soldera" },
        component: EmailTemplatesView,
        beforeEnter: [authGuard, adminGuard],
      },
      {
        path: "company-orders",
        name: "AdminCompanyOrdersView",
        meta: { title: "Company Orders - Admin - Soldera" },
        component: AdminCompanyOrdersView,
        beforeEnter: [authGuard, adminGuard],
      },
      {
        path: "dashboard",
        name: "AdminDashboardDashboard",
        meta: { title: "Operations Dashboard - Admin - Soldera" },
        component: AdminDashboardView,
        beforeEnter: [authGuard, adminGuard],
      },
      {
        path: "map",
        name: "AdminMapDashboard",
        meta: { title: "Device Map - Admin - Soldera" },
        component: MapView,
        beforeEnter: [authGuard, adminGuard],
      },
      {
        path: "transactions",
        beforeEnter: [authGuard, adminGuard],
        children: [
          {
            path: "",
            name: "AdminTransactionsListView",
            meta: { title: "Transactions - Admin - Soldera" },
            component: TransactionsListView,
            beforeEnter: [authGuard, adminGuard],
          },
          {
            path: "new",
            name: "AdminTransactionsNewView",
            meta: { title: "New Transaction - Admin - Soldera" },
            component: TransactionsNewView,
            beforeEnter: [authGuard, adminGuard],
          },
          {
            // Forwards v2 deal creation: future inventory beside the spot sale of existing inventory.
            path: "new-forward",
            name: "AdminTransactionsNewForwardView",
            meta: { title: "New Transaction - Admin - Soldera" },
            component: VolumePickerView,
            beforeEnter: [authGuard, adminGuard],
          },
          {
            path: ":id/edit",
            name: "AdminTransactionsEditView",
            meta: { title: "Edit Transaction - Admin - Soldera" },
            component: TransactionsNewView,
            beforeEnter: [authGuard, adminGuard],
          },
          {
            path: "performance",
            name: "AdminTransactionsPerformanceView",
            meta: { title: "Transaction Performance - Admin - Soldera" },
            component: TransactionPerformanceView,
            beforeEnter: [authGuard, adminGuard],
          },
        ],
      },
      {
        path: "billing",
        name: "AdminBillingView",
        meta: { title: "Billing - Admin - Soldera" },
        component: BillingView,
        beforeEnter: [authGuard, adminGuard],
      },
      // Legacy routes merged into the Billing workspace — redirects keep old
      // bookmarks working and map each page onto its tab.
      {
        path: "finances",
        redirect: (to) => {
          const { "admin-finances-active-tab": legacyTab, ...query } = to.query;
          return {
            name: "AdminBillingView",
            query: {
              ...query,
              "admin-billing-active-tab":
                legacyTab === "service-fee-report" ? "reports" : "invoices",
            },
          };
        },
      },
      {
        path: "invoicing",
        redirect: (to) => ({
          name: "AdminBillingView",
          query: { ...to.query, "admin-billing-active-tab": "company" },
        }),
      },
      {
        path: "corrections",
        redirect: (to) => ({
          name: "AdminBillingView",
          query: { ...to.query, "admin-billing-active-tab": "corrections" },
        }),
      },
      {
        path: "financial-dashboard",
        name: "AdminFinancialDashboardView",
        meta: { title: "Finance Dashboard - Admin - Soldera" },
        component: FinancialDashboardView,
        beforeEnter: [authGuard, adminGuard],
      },
      {
        path: "registry-devices",
        name: "AdminRegistryDevicesView",
        meta: { title: "Registry Devices - Admin - Soldera" },
        component: AdminRegistryDevicesView,
        beforeEnter: [authGuard, adminGuard],
      },
      {
        path: "ecolabel-export",
        name: "EcolabelManagementView",
        meta: { title: "Ecolabel Management - Admin - Soldera" },
        component: EcolabelManagementView,
        beforeEnter: [authGuard, adminGuard],
      },
      {
        path: "global-companies",
        name: "AdminGlobalCompaniesView",
        meta: { title: "Global Companies - Admin - Soldera" },
        component: GlobalCompaniesView,
        beforeEnter: [authGuard, adminGuard],
      },
      {
        path: "eac-pricing",
        name: "AdminEacPricingView",
        meta: { title: "EAC Pricing - Admin - Soldera" },
        component: EacPricingManagementView,
        beforeEnter: [authGuard, adminGuard],
      },
      {
        path: "registries",
        name: "AdminRegistriesView",
        meta: { title: "Registries - Admin - Soldera" },
        component: AdminRegistriesView,
        beforeEnter: [authGuard],
      },
      {
        path: "commission",
        name: "AdminCommissionView",
        meta: { title: "Commission - Admin - Soldera" },
        component: CommissionView,
        beforeEnter: [authGuard],
      },
      {
        path: "issuance",
        name: "AdminIssuanceView",
        meta: { title: "Issuance - Admin - Soldera" },
        component: IssuanceView,
        beforeEnter: [authGuard, adminGuard],
      },
      {
        path: "eso",
        beforeEnter: [authGuard, adminGuard],
        children: [
          {
            path: "info",
            name: "EsoInfoView",
            meta: { title: "ESO Info - Admin - Soldera" },
            component: EsoInfoView,
            beforeEnter: [authGuard, adminGuard],
          },
          {
            path: "mapping",
            name: "EsoMappingView",
            meta: { title: "ESO Mapping - Admin - Soldera" },
            component: EsoMappingView,
            beforeEnter: [authGuard, adminGuard],
          },
          {
            path: "power-bi",
            name: "EsoPowerBiView",
            meta: { title: "ESO Power BI - Admin - Soldera" },
            component: EsoPowerBiView,
            beforeEnter: [authGuard, adminGuard],
          },
          {
            path: "status",
            name: "EsoStatusView",
            meta: { title: "ESO Status - Admin - Soldera" },
            component: EsoStatusView,
            beforeEnter: [authGuard, adminGuard],
          },
        ],
      },
    ],
  },
  {
    path: "/callback",
    name: "Callback",
    meta: { title: "Soldera" },
    component: CallbackView,
  },
  {
    path: "/:pathMatch(.*)*",
    redirect: { name: "Root" },
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

// Restricted portals expose a reduced route surface (see @/portals); blocked
// navigation lands on the portal's fallback route.
router.beforeEach(portalNavigationRedirect);

router.beforeEach(markAccountStaleOnPageChange);

router.afterEach((to) => {
  nextTick(() => {
    // Route meta titles are Soldera-branded; white-label hosts keep the
    // brand's own title everywhere.
    const title =
      !isWhiteLabel() && typeof to.meta.title === "string"
        ? to.meta.title
        : getBranding().title;
    document.title = title;
  });

  // The support chat is Soldera-branded (name, face, copy) — keep it off
  // white-label hosts. Re-asserted on every navigation because the widget
  // shows itself when its script finishes loading.
  if (
    window.HelpCrunch &&
    (isWhiteLabel() || to.matched.some((r) => r.path === "/admin"))
  ) {
    window.HelpCrunch("hideChatWidget");
  }
});

export default router;
